import React, { useEffect, useState, useCallback } from 'react';
import {
  PlusIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  CheckCircleIcon,
  PaperClipIcon,
  ClockIcon,
  TrashIcon,
  PencilSquareIcon,
  DocumentDuplicateIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
  ArrowUpTrayIcon,
  ArrowDownTrayIcon,
  LinkIcon,
  CalendarIcon,
  BarsArrowDownIcon,
  BarsArrowUpIcon,
  Bars3Icon,
  PlayIcon,
  PauseIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { CheckCircleIcon as CheckCircleSolid } from '@heroicons/react/24/solid';
import { useTaskStore } from '../store/taskStore';
import { useProjectStore } from '../store/projectStore';
import { useClientStore } from '../store/clientStore';
import { useTaskTimerStore, formatElapsed } from '../store/taskTimerStore';
import {
  tasksAPI,
  taskAttachmentsAPI,
  taskTimeEntriesAPI,
  taskLinksAPI,
  timeEntriesAPI,
  TaskItem,
  TaskWithSubtasks,
  TaskCreate,
  TaskUpdate,
  TaskStatus,
  TaskPriority,
  TaskAttachment,
  TaskTimeEntryLink,
  TaskLinkItem,
  TimeEntry,
  categoriesAPI,
  subprojectsAPI,
  ProjectSubproject,
} from '../services/api';

// ===== Constants =====

const STATUS_CONFIG: Record<TaskStatus, { label: string; color: string; bgColor: string }> = {
  todo: { label: 'To Do', color: 'text-gray-600 dark:text-gray-400', bgColor: 'bg-gray-100 dark:bg-gray-700' },
  in_progress: { label: 'In Progress', color: 'text-blue-600 dark:text-blue-400', bgColor: 'bg-blue-100 dark:bg-blue-900' },
  blocked: { label: 'Blocked', color: 'text-red-600 dark:text-red-400', bgColor: 'bg-red-100 dark:bg-red-900' },
  in_review: { label: 'In Review', color: 'text-purple-600 dark:text-purple-400', bgColor: 'bg-purple-100 dark:bg-purple-900' },
  done: { label: 'Done', color: 'text-green-600 dark:text-green-400', bgColor: 'bg-green-100 dark:bg-green-900' },
  cancelled: { label: 'Cancelled', color: 'text-gray-400 dark:text-gray-500', bgColor: 'bg-gray-50 dark:bg-gray-800' },
};

const PRIORITY_CONFIG: Record<TaskPriority, { label: string; color: string; dotColor: string }> = {
  urgent: { label: 'Urgent', color: 'text-red-600', dotColor: 'bg-red-500' },
  high: { label: 'High', color: 'text-orange-600', dotColor: 'bg-orange-500' },
  medium: { label: 'Medium', color: 'text-yellow-600', dotColor: 'bg-yellow-500' },
  low: { label: 'Low', color: 'text-blue-600', dotColor: 'bg-blue-500' },
  none: { label: 'None', color: 'text-gray-500', dotColor: 'bg-gray-400' },
};

const STATUS_ORDER: TaskStatus[] = ['todo', 'in_progress', 'blocked', 'in_review', 'done', 'cancelled'];

type SortField = 'default' | 'priority' | 'subproject' | 'date_created';
type SortDir = 'asc' | 'desc';

const PRIORITY_ORDER: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3, none: 4 };

const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: 'default', label: 'Default' },
  { value: 'priority', label: 'Priority' },
  { value: 'subproject', label: 'Subproject' },
  { value: 'date_created', label: 'Date Added' },
];

const PRIORITY_LABELS: Record<string, string> = { urgent: 'Urgent', high: 'High', medium: 'Medium', low: 'Low', none: 'None' };
const PRIORITY_COLORS: Record<string, string> = {
  urgent: 'text-red-600 dark:text-red-400',
  high: 'text-orange-600 dark:text-orange-400',
  medium: 'text-yellow-600 dark:text-yellow-400',
  low: 'text-blue-600 dark:text-blue-400',
  none: 'text-gray-500 dark:text-gray-400',
};

const getGroupKey = (task: TaskItem, sortField: SortField): string => {
  if (sortField === 'priority') return task.priority || 'none';
  if (sortField === 'subproject') return task.subproject_name || '(No subproject)';
  if (sortField === 'date_created') {
    const d = new Date(task.date_created);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }
  return '';
};

const getGroupLabel = (key: string, sortField: SortField): string => {
  if (sortField === 'priority') return PRIORITY_LABELS[key] || key;
  return key;
};

const getGroupColor = (key: string, sortField: SortField): string => {
  if (sortField === 'priority') return PRIORITY_COLORS[key] || 'text-gray-500 dark:text-gray-400';
  return 'text-gray-500 dark:text-gray-400';
};

// ===== Helper Functions =====

const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  const d = new Date(dateStr + (dateStr.length === 10 ? 'T00:00:00' : ''));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const isOverdue = (dateStr: string | null | undefined, status: TaskStatus): boolean => {
  if (!dateStr || status === 'done' || status === 'cancelled') return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateStr + 'T00:00:00');
  return due < today;
};

const isDueToday = (dateStr: string | null | undefined): boolean => {
  if (!dateStr) return false;
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return dateStr === todayStr;
};

const formatFileSize = (bytes: number): string => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const getTodayString = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// ===== Main Component =====

export const TasksPage: React.FC = () => {
  const {
    tasks, summary, selectedProjectId, statusFilter, searchQuery, isLoading, error,
    fetchTasks, fetchSummary, createTask, updateTask, deleteTask, updateStatus,
    setSelectedProjectId, setStatusFilter, setSearchQuery, clearError,
  } = useTaskStore();

  const { projects, fetchProjects } = useProjectStore();
  const { clients, fetchClients } = useClientStore();

  // Local state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedTaskDetail, setSelectedTaskDetail] = useState<TaskWithSubtasks | null>(null);
  const [editingTask, setEditingTask] = useState<TaskItem | null>(null);
  const [expandedTasks, setExpandedTasks] = useState<Set<string>>(new Set());
  const [subtasksMap, setSubtasksMap] = useState<Record<string, TaskItem[]>>({});
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const [quickAddPriority, setQuickAddPriority] = useState<TaskPriority>('high');
  const [quickAddSubproject, setQuickAddSubproject] = useState('');
  const [categories, setCategories] = useState<any[]>([]);
  const [subprojects, setSubprojects] = useState<ProjectSubproject[]>([]);
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [sortByStatus, setSortByStatus] = useState<Record<string, { field: SortField; dir: SortDir }>>({});

  // Task timers
  const taskTimerStore = useTaskTimerStore();
  const [timerDisplays, setTimerDisplays] = useState<Record<string, string>>({});
  const [timerSuccessMsg, setTimerSuccessMsg] = useState('');

  const getSortFor = (status: string) => sortByStatus[status] || { field: 'priority' as SortField, dir: 'asc' as SortDir };
  const setSortFieldFor = (status: string, field: SortField) => setSortByStatus(prev => ({ ...prev, [status]: { ...getSortFor(status), field } }));
  const toggleSortDirFor = (status: string) => setSortByStatus(prev => ({ ...prev, [status]: { ...getSortFor(status), dir: getSortFor(status).dir === 'asc' ? 'desc' : 'asc' } }));

  // Detail modal state
  const [detailAttachments, setDetailAttachments] = useState<TaskAttachment[]>([]);
  const [detailUrlLinks, setDetailUrlLinks] = useState<TaskLinkItem[]>([]);
  const [detailTimeEntries, setDetailTimeEntries] = useState<TaskTimeEntryLink[]>([]);
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);

  // Log time modal
  const [showLogTimeModal, setShowLogTimeModal] = useState(false);
  const [logTimeData, setLogTimeData] = useState({ date: getTodayString(), description: '', hours: '' });

  // Link time entry modal
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [availableEntries, setAvailableEntries] = useState<TimeEntry[]>([]);
  const [selectedEntryId, setSelectedEntryId] = useState('');

  useEffect(() => {
    fetchProjects();
    fetchClients();
  }, [fetchProjects, fetchClients]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Fetch categories and subprojects when project changes
  useEffect(() => {
    if (selectedProjectId) {
      categoriesAPI.list(selectedProjectId).then(setCategories).catch(() => {});
      subprojectsAPI.list(selectedProjectId).then(setSubprojects).catch(() => {});
    } else {
      setCategories([]);
      setSubprojects([]);
    }
  }, [selectedProjectId]);

  // Tick timer displays every second
  useEffect(() => {
    const tick = () => {
      const state = useTaskTimerStore.getState();
      const displays: Record<string, string> = {};
      Object.keys(state.timers).forEach(id => {
        displays[id] = formatElapsed(state.getElapsed(id));
      });
      setTimerDisplays(displays);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  // Sort comparator (per-status)
  const sortTasks = useCallback((list: TaskItem[], field: SortField, dir: SortDir): TaskItem[] => {
    if (field === 'default') return list;
    const sorted = [...list].sort((a, b) => {
      let cmp = 0;
      if (field === 'priority') {
        cmp = (PRIORITY_ORDER[a.priority] ?? 4) - (PRIORITY_ORDER[b.priority] ?? 4);
      } else if (field === 'subproject') {
        const sa = (a.subproject_name || '').toLowerCase();
        const sb = (b.subproject_name || '').toLowerCase();
        if (!sa && !sb) cmp = 0;
        else if (!sa) cmp = 1;
        else if (!sb) cmp = -1;
        else cmp = sa.localeCompare(sb);
      } else if (field === 'date_created') {
        cmp = new Date(a.date_created).getTime() - new Date(b.date_created).getTime();
      }
      return dir === 'desc' ? -cmp : cmp;
    });
    return sorted;
  }, []);

  // Group tasks by status with per-status sorting
  const tasksByStatus = STATUS_ORDER.reduce((acc, s) => {
    const { field, dir } = getSortFor(s);
    acc[s] = sortTasks(tasks.filter(t => t.status === s), field, dir);
    return acc;
  }, {} as Record<TaskStatus, TaskItem[]>);

  const filteredStatuses = statusFilter
    ? STATUS_ORDER.filter(s => s === statusFilter)
    : STATUS_ORDER.filter(s => tasksByStatus[s].length > 0 || s === 'todo' || s === 'in_progress' || s === 'done');

  // Toggle subtasks
  const toggleSubtasks = async (taskId: string) => {
    const newExpanded = new Set(expandedTasks);
    if (newExpanded.has(taskId)) {
      newExpanded.delete(taskId);
    } else {
      newExpanded.add(taskId);
      if (!subtasksMap[taskId]) {
        try {
          const subs = await tasksAPI.list({ parent_task_id: taskId });
          setSubtasksMap(prev => ({ ...prev, [taskId]: subs }));
        } catch { /* ignore */ }
      }
    }
    setExpandedTasks(newExpanded);
  };

  // Open task detail
  const openTaskDetail = async (taskId: string) => {
    try {
      const task = await tasksAPI.get(taskId);
      setSelectedTaskDetail(task);
      setShowDetailModal(true);
      // Load attachments, links, and time entries
      const [atts, links, entries] = await Promise.all([
        taskAttachmentsAPI.list(taskId),
        taskLinksAPI.list(taskId),
        taskTimeEntriesAPI.list(taskId),
      ]);
      setDetailAttachments(atts);
      setDetailUrlLinks(links);
      setDetailTimeEntries(entries);
    } catch { /* ignore */ }
  };

  // Handle checkbox toggle (done/todo)
  const handleCheckboxToggle = async (task: TaskItem) => {
    const newStatus: TaskStatus = task.status === 'done' ? 'todo' : 'done';
    await updateStatus(task.id, newStatus);
    // Refresh subtasks if visible
    if (task.parent_task_id && subtasksMap[task.parent_task_id]) {
      const subs = await tasksAPI.list({ parent_task_id: task.parent_task_id });
      setSubtasksMap(prev => ({ ...prev, [task.parent_task_id!]: subs }));
    }
  };

  // Stop a task timer and auto-create a linked time entry
  const handleTaskTimerStop = async (taskId: string) => {
    const result = taskTimerStore.stopTimer(taskId);
    if (!result || result.hours <= 0) return;
    try {
      await taskTimeEntriesAPI.logTime(taskId, {
        date: getTodayString(),
        description: result.taskTitle,
        hours: result.hours,
        category: result.categoryName || undefined,
        subproject_id: result.subprojectId || undefined,
      });
      fetchTasks();
      const msg = `${result.hours.toFixed(2)}h logged for "${result.taskTitle}"`;
      setTimerSuccessMsg(msg);
      setTimeout(() => setTimerSuccessMsg(''), 4000);
    } catch (err) {
      console.error('Failed to create time entry from task timer:', err);
    }
  };

  // Quick add (always creates as 'todo')
  const handleQuickAdd = async () => {
    if (!quickAddTitle.trim() || !selectedProjectId) return;
    try {
      await createTask({
        project_id: selectedProjectId,
        title: quickAddTitle.trim(),
        status: 'todo',
        priority: quickAddPriority,
        subproject_id: quickAddSubproject || undefined,
      });
      setQuickAddTitle('');
      setQuickAddPriority('high');
      setQuickAddSubproject('');
      setQuickAddOpen(false);
    } catch { /* ignore */ }
  };

  // Drag-and-drop handlers
  const handleDragStart = (taskId: string) => {
    setDraggedTaskId(taskId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, targetStatus: TaskStatus) => {
    e.preventDefault();
    if (!draggedTaskId) return;
    const task = tasks.find(t => t.id === draggedTaskId);
    if (task && task.status !== targetStatus) {
      await updateStatus(draggedTaskId, targetStatus);
    }
    setDraggedTaskId(null);
  };

  const handleDragEnd = () => {
    setDraggedTaskId(null);
  };

  // Attachment upload
  const handleAttachmentUpload = async (taskId: string, files: FileList) => {
    setIsUploadingAttachment(true);
    try {
      for (const file of Array.from(files)) {
        if (file.size > 10 * 1024 * 1024) continue; // skip >10MB
        await taskAttachmentsAPI.upload(taskId, file);
      }
      const atts = await taskAttachmentsAPI.list(taskId);
      setDetailAttachments(atts);
      fetchTasks();
    } catch { /* ignore */ }
    setIsUploadingAttachment(false);
  };

  // Attachment download
  const handleAttachmentDownload = async (att: TaskAttachment) => {
    try {
      const blob = await taskAttachmentsAPI.download(att.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  // Attachment delete
  const handleAttachmentDelete = async (attachmentId: string, taskId: string) => {
    try {
      await taskAttachmentsAPI.delete(attachmentId);
      const atts = await taskAttachmentsAPI.list(taskId);
      setDetailAttachments(atts);
      fetchTasks();
    } catch { /* ignore */ }
  };

  // Log time
  const handleLogTime = async () => {
    if (!selectedTaskDetail || !logTimeData.description.trim() || !logTimeData.hours) return;
    try {
      await taskTimeEntriesAPI.logTime(selectedTaskDetail.id, {
        date: logTimeData.date,
        description: logTimeData.description.trim(),
        hours: parseFloat(logTimeData.hours),
      });
      const entries = await taskTimeEntriesAPI.list(selectedTaskDetail.id);
      setDetailTimeEntries(entries);
      setShowLogTimeModal(false);
      setLogTimeData({ date: getTodayString(), description: '', hours: '' });
      fetchTasks();
    } catch { /* ignore */ }
  };

  // Link existing time entry
  const handleLinkEntry = async () => {
    if (!selectedTaskDetail || !selectedEntryId) return;
    try {
      await taskTimeEntriesAPI.link(selectedTaskDetail.id, selectedEntryId);
      const entries = await taskTimeEntriesAPI.list(selectedTaskDetail.id);
      setDetailTimeEntries(entries);
      setShowLinkModal(false);
      setSelectedEntryId('');
      fetchTasks();
    } catch { /* ignore */ }
  };

  // Unlink time entry
  const handleUnlinkEntry = async (timeEntryId: string) => {
    if (!selectedTaskDetail) return;
    try {
      await taskTimeEntriesAPI.unlink(selectedTaskDetail.id, timeEntryId);
      const entries = await taskTimeEntriesAPI.list(selectedTaskDetail.id);
      setDetailTimeEntries(entries);
      fetchTasks();
    } catch { /* ignore */ }
  };

  // Open link modal
  const openLinkModal = async () => {
    if (!selectedTaskDetail) return;
    try {
      const entries = await timeEntriesAPI.list({ project_id: selectedTaskDetail.project_id });
      setAvailableEntries(entries);
      setShowLinkModal(true);
    } catch { /* ignore */ }
  };

  // Active project list (grouped by client)
  const activeProjects = projects.filter(p => p.is_active);
  const projectsByClient: Record<string, typeof activeProjects> = {};
  activeProjects.forEach(p => {
    const cName = clients.find(c => c.id === p.client_id)?.name || 'No Client';
    if (!projectsByClient[cName]) projectsByClient[cName] = [];
    projectsByClient[cName].push(p);
  });

  // ===== Render =====

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto">
      {/* Timer success toast */}
      {timerSuccessMsg && (
        <div className="fixed top-4 right-4 z-50 bg-green-600 text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-lg flex items-center gap-2 animate-fade-in">
          <ClockIcon className="h-4 w-4 flex-shrink-0" />
          {timerSuccessMsg}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Tasks</h1>
        <button
          onClick={() => { setEditingTask(null); setShowAddModal(true); }}
          className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Add Task
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4 mb-6 space-y-4">
        {/* Project selector */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Project</label>
            <select
              value={selectedProjectId || ''}
              onChange={(e) => setSelectedProjectId(e.target.value || null)}
              className="w-full rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm py-2 px-3"
            >
              <option value="">All Projects</option>
              {Object.entries(projectsByClient).map(([clientName, projs]) => (
                <optgroup key={clientName} label={clientName}>
                  {projs.map(p => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>

          {/* Search */}
          <div className="flex-1">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Search</label>
            <div className="relative">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search tasks..."
                className="w-full pl-9 rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm py-2 px-3"
              />
            </div>
          </div>
        </div>

        {/* Status filter chips */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setStatusFilter(null)}
            className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              !statusFilter
                ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-200'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            All {summary ? `(${summary.total})` : ''}
          </button>
          {STATUS_ORDER.map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(statusFilter === s ? null : s)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                statusFilter === s
                  ? `${STATUS_CONFIG[s].bgColor} ${STATUS_CONFIG[s].color}`
                  : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
              }`}
            >
              {STATUS_CONFIG[s].label} {summary?.by_status?.[s] ? `(${summary.by_status[s]})` : ''}
            </button>
          ))}
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 rounded-lg p-3 mb-4 flex items-center justify-between">
          <span className="text-sm text-red-700 dark:text-red-300">{error}</span>
          <button onClick={clearError}><XMarkIcon className="h-4 w-4 text-red-500" /></button>
        </div>
      )}

      {/* No project selected hint */}
      {!selectedProjectId && tasks.length === 0 && !isLoading && (
        <div className="text-center py-12 text-gray-500 dark:text-gray-400">
          <FunnelIcon className="h-12 w-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
          <p className="text-lg font-medium mb-1">Select a project to get started</p>
          <p className="text-sm">Choose a project from the dropdown above to view and manage tasks.</p>
        </div>
      )}

      {/* Task List grouped by status */}
      {filteredStatuses.map(statusKey => (
        <div
          key={statusKey}
          className={`mb-6 rounded-lg transition-colors ${draggedTaskId ? 'border-2 border-dashed border-transparent' : ''}`}
          onDragOver={handleDragOver}
          onDrop={(e) => handleDrop(e, statusKey)}
          style={draggedTaskId ? { borderColor: statusKey === 'todo' ? '#9CA3AF' : statusKey === 'in_progress' ? '#3B82F6' : statusKey === 'done' ? '#22C55E' : statusKey === 'blocked' ? '#EF4444' : statusKey === 'in_review' ? '#8B5CF6' : '#6B7280' } : undefined}
        >
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="inline-block w-3 h-3 rounded-full"
                style={{ backgroundColor: statusKey === 'todo' ? '#9CA3AF' : statusKey === 'in_progress' ? '#3B82F6' : statusKey === 'blocked' ? '#EF4444' : statusKey === 'in_review' ? '#8B5CF6' : statusKey === 'done' ? '#22C55E' : '#6B7280' }}
              />
              <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                {STATUS_CONFIG[statusKey].label}
              </h2>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {tasksByStatus[statusKey].length} {tasksByStatus[statusKey].length === 1 ? 'task' : 'tasks'}
              </span>
            </div>
            {tasksByStatus[statusKey].length > 1 && (
              <div className="flex items-center gap-1.5 flex-shrink-0">
                <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">Sort by</span>
                <select
                  value={getSortFor(statusKey).field}
                  onChange={(e) => setSortFieldFor(statusKey, e.target.value as SortField)}
                  className="text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-1 pl-2 pr-6"
                >
                  {SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
                {getSortFor(statusKey).field !== 'default' && (
                  <button
                    onClick={() => toggleSortDirFor(statusKey)}
                    className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-500 dark:text-gray-400"
                    title={getSortFor(statusKey).dir === 'asc' ? 'Ascending — click to reverse' : 'Descending — click to reverse'}
                  >
                    {getSortFor(statusKey).dir === 'asc' ? <BarsArrowDownIcon className="h-4 w-4" /> : <BarsArrowUpIcon className="h-4 w-4" />}
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="space-y-1">
            {(() => {
              const sortCfg = getSortFor(statusKey);
              const tasksInGroup = tasksByStatus[statusKey];
              let lastGroupKey = '';
              return tasksInGroup.map((task, idx) => {
                const groupKey = sortCfg.field !== 'default' ? getGroupKey(task, sortCfg.field) : '';
                const showDivider = sortCfg.field !== 'default' && groupKey !== lastGroupKey;
                lastGroupKey = groupKey;
                return (
                  <React.Fragment key={task.id}>
                    {showDivider && (
                      <div className={`flex items-center gap-2 ${idx > 0 ? 'mt-3' : ''}`}>
                        <span className={`text-xs font-semibold uppercase tracking-wide ${getGroupColor(groupKey, sortCfg.field)}`}>
                          {getGroupLabel(groupKey, sortCfg.field)}
                        </span>
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                      </div>
                    )}
                    <TaskCard
                      task={task}
                      isExpanded={expandedTasks.has(task.id)}
                      subtasks={subtasksMap[task.id]}
                      onToggleExpand={() => toggleSubtasks(task.id)}
                      onCheckbox={() => handleCheckboxToggle(task)}
                      onClickTitle={() => openTaskDetail(task.id)}
                      onEdit={() => { setEditingTask(task); setShowAddModal(true); }}
                      onDelete={() => { if (window.confirm('Delete this task and all subtasks?')) deleteTask(task.id); }}
                      onDuplicate={() => tasksAPI.duplicate(task.id, true).then(() => fetchTasks())}
                      onSubtaskCheckbox={handleCheckboxToggle}
                      onSubtaskClick={(id) => openTaskDetail(id)}
                      onSubtaskEdit={(st) => { setEditingTask(st); setShowAddModal(true); }}
                      onSubtaskDelete={(st) => { if (window.confirm('Delete this subtask?')) { deleteTask(st.id).then(() => { setSubtasksMap(prev => { const next = { ...prev }; delete next[task.id]; return next; }); }); } }}
                      onDragStart={() => handleDragStart(task.id)}
                      onDragEnd={handleDragEnd}
                      getTimerState={taskTimerStore.getTimerState}
                      getTimerDisplay={(id) => timerDisplays[id] || '00:00:00'}
                      onTimerStart={taskTimerStore.startTimer}
                      onTimerPause={taskTimerStore.pauseTimer}
                      onTimerResume={taskTimerStore.resumeTimer}
                      onTimerStop={handleTaskTimerStop}
                      onTimerDiscard={taskTimerStore.discardTimer}
                    />
                  </React.Fragment>
                );
              });
            })()}
          </div>

          {/* Quick add — only in the To Do section */}
          {statusKey === 'todo' && (
            <div className="mt-2">
              {!selectedProjectId ? (
                <p className="pl-8 text-xs text-gray-400 dark:text-gray-500 italic py-1">Select a project above to quick-add tasks</p>
              ) : quickAddOpen ? (
                <div className="space-y-2 pl-8 pr-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={quickAddTitle}
                      onChange={(e) => setQuickAddTitle(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleQuickAdd(); if (e.key === 'Escape') { setQuickAddOpen(false); setQuickAddTitle(''); setQuickAddPriority('high'); setQuickAddSubproject(''); } }}
                      placeholder="Task title..."
                      className="flex-1 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-1.5 px-3 focus:ring-2 focus:ring-green-500 focus:border-transparent"
                      autoFocus
                    />
                    <button onClick={handleQuickAdd} disabled={!quickAddTitle.trim()} className="text-green-600 hover:text-green-700 text-sm font-medium disabled:opacity-50">Add</button>
                    <button onClick={() => { setQuickAddOpen(false); setQuickAddTitle(''); setQuickAddPriority('high'); setQuickAddSubproject(''); }} className="text-gray-400 hover:text-gray-500 dark:hover:text-gray-300">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    <select
                      value={quickAddPriority}
                      onChange={(e) => setQuickAddPriority(e.target.value as TaskPriority)}
                      className="text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-1 px-2"
                    >
                      {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label} priority</option>)}
                    </select>
                    {subprojects.length > 0 && (
                      <select
                        value={quickAddSubproject}
                        onChange={(e) => setQuickAddSubproject(e.target.value)}
                        className="text-xs rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-1 px-2"
                      >
                        <option value="">No subproject</option>
                        {subprojects.filter(sp => sp.is_active).map(sp => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
                      </select>
                    )}
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => setQuickAddOpen(true)}
                  className="flex items-center gap-1 pl-8 text-sm text-gray-400 hover:text-green-600 dark:text-gray-500 dark:hover:text-green-400 py-1"
                >
                  <PlusIcon className="h-3.5 w-3.5" />
                  <span>Add task</span>
                </button>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Loading */}
      {isLoading && tasks.length === 0 && (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600 mx-auto"></div>
          <p className="mt-3 text-sm text-gray-500">Loading tasks...</p>
        </div>
      )}

      {/* Add/Edit Task Modal */}
      {showAddModal && (
        <AddEditTaskModal
          task={editingTask}
          projectId={selectedProjectId}
          projects={activeProjects}
          projectsByClient={projectsByClient}
          categories={categories}
          onSave={async (data) => {
            if (editingTask) {
              await updateTask(editingTask.id, data as TaskUpdate);
              // Invalidate cached subtasks so expansion fetches fresh data
              setSubtasksMap(prev => {
                const next = { ...prev };
                delete next[editingTask.id];
                // Also invalidate parent's cache if editing a subtask
                if (editingTask.parent_task_id) {
                  delete next[editingTask.parent_task_id];
                }
                return next;
              });
            } else {
              await createTask(data as TaskCreate);
            }
            setShowAddModal(false);
            setEditingTask(null);
          }}
          onClose={() => { setShowAddModal(false); setEditingTask(null); }}
        />
      )}

      {/* Task Detail Modal */}
      {showDetailModal && selectedTaskDetail && (
        <TaskDetailModal
          task={selectedTaskDetail}
          attachments={detailAttachments}
          urlLinks={detailUrlLinks}
          timeEntries={detailTimeEntries}
          categories={categories}
          isUploadingAttachment={isUploadingAttachment}
          onClose={() => { setShowDetailModal(false); setSelectedTaskDetail(null); setDetailAttachments([]); setDetailUrlLinks([]); setDetailTimeEntries([]); }}
          onUpdateStatus={async (s) => {
            await updateStatus(selectedTaskDetail.id, s);
            const updated = await tasksAPI.get(selectedTaskDetail.id);
            setSelectedTaskDetail(updated);
          }}
          onEdit={() => { setEditingTask(selectedTaskDetail); setShowAddModal(true); setShowDetailModal(false); }}
          onDelete={() => { if (window.confirm('Delete this task?')) { deleteTask(selectedTaskDetail.id); setShowDetailModal(false); } }}
          onUploadAttachment={(files) => handleAttachmentUpload(selectedTaskDetail.id, files)}
          onDownloadAttachment={handleAttachmentDownload}
          onDeleteAttachment={(id) => handleAttachmentDelete(id, selectedTaskDetail.id)}
          onAddUrlLink={async (url, title) => {
            const link = await taskLinksAPI.create(selectedTaskDetail.id, { url, title });
            setDetailUrlLinks(prev => [...prev, link]);
          }}
          onDeleteUrlLink={async (linkId) => {
            await taskLinksAPI.delete(linkId);
            setDetailUrlLinks(prev => prev.filter(l => l.id !== linkId));
          }}
          onLogTime={() => setShowLogTimeModal(true)}
          onLinkEntry={openLinkModal}
          onUnlinkEntry={handleUnlinkEntry}
          onAddSubtask={async (title) => {
            await createTask({
              project_id: selectedTaskDetail.project_id,
              parent_task_id: selectedTaskDetail.id,
              title,
              priority: selectedTaskDetail.priority,
              subproject_id: selectedTaskDetail.subproject_id ?? undefined,
              category_id: selectedTaskDetail.category_id ?? undefined,
            });
            const updated = await tasksAPI.get(selectedTaskDetail.id);
            setSelectedTaskDetail(updated);
          }}
          onSubtaskStatusToggle={async (subtask) => {
            const newStatus: TaskStatus = subtask.status === 'done' ? 'todo' : 'done';
            await updateStatus(subtask.id, newStatus);
            const updated = await tasksAPI.get(selectedTaskDetail.id);
            setSelectedTaskDetail(updated);
          }}
        />
      )}

      {/* Log Time Modal */}
      {showLogTimeModal && selectedTaskDetail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowLogTimeModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Log Time</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Date</label>
                <input type="date" value={logTimeData.date} onChange={e => setLogTimeData(d => ({ ...d, date: e.target.value }))}
                  className="w-full rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm dark:[color-scheme:dark]" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <input type="text" value={logTimeData.description} onChange={e => setLogTimeData(d => ({ ...d, description: e.target.value }))}
                  placeholder="What did you work on?"
                  className="w-full rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Hours</label>
                <input type="number" step="0.25" min="0.25" max="24" value={logTimeData.hours}
                  onChange={e => setLogTimeData(d => ({ ...d, hours: e.target.value }))}
                  className="w-full rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowLogTimeModal(false)} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleLogTime} disabled={!logTimeData.description.trim() || !logTimeData.hours}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">Log Time</button>
            </div>
          </div>
        </div>
      )}

      {/* Link Time Entry Modal */}
      {showLinkModal && selectedTaskDetail && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowLinkModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Link Time Entry</h3>
            <select value={selectedEntryId} onChange={e => setSelectedEntryId(e.target.value)}
              className="w-full rounded-lg border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm mb-4">
              <option value="">Select a time entry...</option>
              {availableEntries.map(e => (
                <option key={e.id} value={e.id}>
                  {e.date} — {e.description.substring(0, 60)} ({e.hours}h)
                </option>
              ))}
            </select>
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowLinkModal(false)} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
              <button onClick={handleLinkEntry} disabled={!selectedEntryId}
                className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">Link</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ===== Task Card Component =====

interface TaskCardProps {
  task: TaskItem;
  isExpanded: boolean;
  subtasks?: TaskItem[];
  indent?: boolean;
  onToggleExpand: () => void;
  onCheckbox: () => void;
  onClickTitle: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onDuplicate: () => void;
  onSubtaskCheckbox: (task: TaskItem) => void;
  onSubtaskClick: (id: string) => void;
  onSubtaskEdit: (task: TaskItem) => void;
  onSubtaskDelete: (task: TaskItem) => void;
  onDragStart: () => void;
  onDragEnd: () => void;
  getTimerState: (id: string) => 'idle' | 'running' | 'paused';
  getTimerDisplay: (id: string) => string;
  onTimerStart: (task: TaskItem) => void;
  onTimerPause: (taskId: string) => void;
  onTimerResume: (taskId: string) => void;
  onTimerStop: (taskId: string) => void;
  onTimerDiscard: (taskId: string) => void;
}

const TaskCard: React.FC<TaskCardProps> = ({
  task, isExpanded, subtasks, indent, onToggleExpand, onCheckbox,
  onClickTitle, onEdit, onDelete, onDuplicate, onSubtaskCheckbox, onSubtaskClick,
  onSubtaskEdit, onSubtaskDelete, onDragStart, onDragEnd,
  getTimerState, getTimerDisplay, onTimerStart, onTimerPause, onTimerResume, onTimerStop, onTimerDiscard,
}) => {
  const isDone = task.status === 'done' || task.status === 'cancelled';
  const priorityCfg = PRIORITY_CONFIG[task.priority];
  const timerState = getTimerState(task.id);
  const timerDisplay = getTimerDisplay(task.id);

  return (
    <div>
      <div
        draggable
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        className={`flex items-center gap-2 py-2 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 group cursor-grab active:cursor-grabbing ${indent ? 'ml-8' : ''} ${isDone ? 'opacity-60' : ''}`}
      >
        {/* Expand toggle */}
        {task.subtask_count > 0 ? (
          <button onClick={onToggleExpand} className="flex-shrink-0 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
            {isExpanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
          </button>
        ) : (
          <span className="w-4 flex-shrink-0" />
        )}

        {/* Checkbox */}
        <button onClick={onCheckbox} className="flex-shrink-0">
          {isDone ? (
            <CheckCircleSolid className="h-5 w-5 text-green-500" />
          ) : (
            <CheckCircleIcon className="h-5 w-5 text-gray-300 dark:text-gray-500 hover:text-green-500 dark:hover:text-green-400" />
          )}
        </button>

        {/* Priority dot */}
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${priorityCfg.dotColor}`} title={`${priorityCfg.label} priority`} />

        {/* Title */}
        <button
          onClick={onClickTitle}
          className={`flex-1 text-left text-sm font-medium truncate ${isDone ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-900 dark:text-white hover:text-green-600 dark:hover:text-green-400'}`}
        >
          {task.title}
        </button>

        {/* Badges */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Priority badge */}
          {task.priority && task.priority !== 'none' && (
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              task.priority === 'urgent' ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400' :
              task.priority === 'high' ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400' :
              task.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/40 text-yellow-600 dark:text-yellow-400' :
              'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
            }`}>
              {PRIORITY_CONFIG[task.priority].label}
            </span>
          )}

          {/* Subproject badge */}
          {task.subproject_name && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400 font-medium">
              {task.subproject_name}
            </span>
          )}

          {/* Category badge */}
          {task.category_name && (
            <span
              className="text-xs px-2 py-0.5 rounded-full text-white font-medium"
              style={{ backgroundColor: task.category_color || '#6B7280' }}
            >
              {task.category_name}
            </span>
          )}

          {/* Date added */}
          <span className="flex items-center text-xs text-gray-500 dark:text-gray-400" title="Date added">
            <CalendarIcon className="h-3 w-3 mr-0.5" />
            {formatDate(task.date_created)}
          </span>

          {/* Due date */}
          {task.date_due && (
            <span className={`text-xs ${
              isOverdue(task.date_due, task.status) ? 'text-red-600 dark:text-red-400 font-medium' :
              isDueToday(task.date_due) ? 'text-yellow-600 dark:text-yellow-400 font-medium' :
              'text-gray-500 dark:text-gray-400'
            }`} title="Due date">
              Due {formatDate(task.date_due)}
            </span>
          )}

          {/* Hours (with subtask rollup) */}
          {(() => {
            const estHours = task.estimated_hours_override || !task.subtask_estimated_hours_total
              ? (task.estimated_hours ?? 0)
              : task.subtask_estimated_hours_total;
            const actHours = task.actual_hours_override || !task.subtask_actual_hours_total
              ? (task.actual_hours ?? 0)
              : task.subtask_actual_hours_total;
            const hasHours = estHours > 0 || actHours > 0;
            if (!hasHours) return null;
            const hasSubtaskHours = (task.subtask_estimated_hours_total ?? 0) > 0 || (task.subtask_actual_hours_total ?? 0) > 0;
            return (
              <span className="text-xs text-gray-500 dark:text-gray-400" title={hasSubtaskHours ? 'Hours (includes subtask totals)' : 'Hours (actual / estimated)'}>
                <ClockIcon className="h-3 w-3 inline mr-0.5" />
                {actHours ? `${actHours}h` : '0h'}
                {estHours ? ` / ${estHours}h` : ''}
                {hasSubtaskHours && !task.estimated_hours_override && <span className="text-green-500 ml-0.5" title="Includes subtask hours">&Sigma;</span>}
              </span>
            );
          })()}

          {/* Subtask progress */}
          {task.subtask_count > 0 && (
            <span className="text-xs text-gray-500 dark:text-gray-400" title="Subtasks completed">
              {task.subtask_done_count}/{task.subtask_count} subtasks
            </span>
          )}

          {/* Attachment count */}
          {task.attachment_count > 0 && (
            <span className="flex items-center text-xs text-gray-500 dark:text-gray-400" title="Attachments">
              <PaperClipIcon className="h-3 w-3 mr-0.5" />{task.attachment_count}
            </span>
          )}

          {/* Time entry count */}
          {task.time_entry_count > 0 && (
            <span className="flex items-center text-xs text-gray-500 dark:text-gray-400" title="Linked time entries">
              <ClockIcon className="h-3 w-3 mr-0.5" />{task.time_entry_count}
            </span>
          )}

          {/* Timer: active display (always visible when running/paused) */}
          {timerState !== 'idle' && (
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className={`font-mono text-xs font-semibold tabular-nums ${timerState === 'paused' ? 'text-yellow-500 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                {timerDisplay}
              </span>
              {timerState === 'paused' ? (
                <button
                  onClick={(e) => { e.stopPropagation(); onTimerResume(task.id); }}
                  className="text-green-500 hover:text-green-600 dark:text-green-400 dark:hover:text-green-300"
                  title="Resume timer"
                >
                  <PlayIcon className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={(e) => { e.stopPropagation(); onTimerPause(task.id); }}
                  className="text-yellow-500 hover:text-yellow-600 dark:text-yellow-400 dark:hover:text-yellow-300"
                  title="Pause timer"
                >
                  <PauseIcon className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={(e) => { e.stopPropagation(); onTimerStop(task.id); }}
                className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300"
                title="Stop & log time entry"
              >
                <StopIcon className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => { e.stopPropagation(); onTimerDiscard(task.id); }}
                className="text-gray-400 hover:text-red-500 dark:text-gray-500 dark:hover:text-red-400"
                title="Discard timer"
              >
                <XMarkIcon className="h-4 w-4" />
              </button>
            </div>
          )}

          {/* Actions (hover-only) */}
          <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {timerState === 'idle' && (
              <button
                onClick={(e) => { e.stopPropagation(); onTimerStart(task); }}
                className="text-gray-400 dark:text-gray-500 hover:text-green-500 dark:hover:text-green-400"
                title="Start timer"
              >
                <PlayIcon className="h-4 w-4" />
              </button>
            )}
            <button onClick={onEdit} className="text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400" title="Edit">
              <PencilSquareIcon className="h-4 w-4" />
            </button>
            <button onClick={onDuplicate} className="text-gray-400 dark:text-gray-500 hover:text-purple-500 dark:hover:text-purple-400" title="Duplicate">
              <DocumentDuplicateIcon className="h-4 w-4" />
            </button>
            <button onClick={onDelete} className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400" title="Delete">
              <TrashIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Subtasks */}
      {isExpanded && subtasks && subtasks.map(st => {
        const stDone = st.status === 'done' || st.status === 'cancelled';
        const stPriorityCfg = PRIORITY_CONFIG[st.priority];
        return (
          <div
            key={st.id}
            className={`flex items-center gap-2 py-1.5 px-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 ml-8 group ${stDone ? 'opacity-60' : ''}`}
          >
            <span className="w-4 flex-shrink-0 text-center text-gray-300 dark:text-gray-600 text-xs">&#8627;</span>
            <button onClick={() => onSubtaskCheckbox(st)} className="flex-shrink-0">
              {stDone ? (
                <CheckCircleSolid className="h-4 w-4 text-green-500" />
              ) : (
                <CheckCircleIcon className="h-4 w-4 text-gray-300 dark:text-gray-500 hover:text-green-500" />
              )}
            </button>
            <span className={`w-2 h-2 rounded-full flex-shrink-0 ${stPriorityCfg.dotColor}`} />
            <button
              onClick={() => onSubtaskClick(st.id)}
              className={`flex-1 text-left text-sm truncate ${stDone ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300 hover:text-green-600 dark:hover:text-green-400'}`}
            >
              {st.title}
            </button>
            <div className="flex items-center gap-2 flex-shrink-0">
              {st.priority && st.priority !== 'none' && st.priority !== 'medium' && (
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                  st.priority === 'urgent' ? 'bg-red-100 dark:bg-red-900/40 text-red-600 dark:text-red-400' :
                  st.priority === 'high' ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-600 dark:text-orange-400' :
                  'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                }`}>
                  {stPriorityCfg.label}
                </span>
              )}
              {st.category_name && (
                <span className="text-xs px-1.5 py-0.5 rounded-full text-white font-medium" style={{ backgroundColor: st.category_color || '#6B7280' }}>
                  {st.category_name}
                </span>
              )}
              {st.date_due && (
                <span className={`text-xs ${isOverdue(st.date_due, st.status) ? 'text-red-600 dark:text-red-400 font-medium' : isDueToday(st.date_due) ? 'text-yellow-600 dark:text-yellow-400 font-medium' : 'text-gray-500 dark:text-gray-400'}`}>
                  Due {formatDate(st.date_due)}
                </span>
              )}
              {((st.estimated_hours ?? 0) > 0 || (st.actual_hours ?? 0) > 0) && (
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  <ClockIcon className="h-3 w-3 inline mr-0.5" />
                  {st.actual_hours ? `${st.actual_hours}h` : '0h'}
                  {st.estimated_hours ? ` / ${st.estimated_hours}h` : ''}
                </span>
              )}
              {st.attachment_count > 0 && (
                <span className="flex items-center text-xs text-gray-500 dark:text-gray-400">
                  <PaperClipIcon className="h-3 w-3 mr-0.5" />{st.attachment_count}
                </span>
              )}
              {/* Subtask timer: active display */}
              {getTimerState(st.id) !== 'idle' && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <span className={`font-mono text-xs font-semibold tabular-nums ${getTimerState(st.id) === 'paused' ? 'text-yellow-500 dark:text-yellow-400' : 'text-green-600 dark:text-green-400'}`}>
                    {getTimerDisplay(st.id)}
                  </span>
                  {getTimerState(st.id) === 'paused' ? (
                    <button onClick={(e) => { e.stopPropagation(); onTimerResume(st.id); }} className="text-green-500 hover:text-green-600 dark:text-green-400" title="Resume timer">
                      <PlayIcon className="h-3.5 w-3.5" />
                    </button>
                  ) : (
                    <button onClick={(e) => { e.stopPropagation(); onTimerPause(st.id); }} className="text-yellow-500 hover:text-yellow-600 dark:text-yellow-400" title="Pause timer">
                      <PauseIcon className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button onClick={(e) => { e.stopPropagation(); onTimerStop(st.id); }} className="text-blue-500 hover:text-blue-600 dark:text-blue-400" title="Stop & log time entry">
                    <StopIcon className="h-3.5 w-3.5" />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); onTimerDiscard(st.id); }} className="text-gray-400 hover:text-red-500 dark:text-gray-500" title="Discard timer">
                    <XMarkIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {getTimerState(st.id) === 'idle' && (
                  <button onClick={(e) => { e.stopPropagation(); onTimerStart(st); }} className="text-gray-400 dark:text-gray-500 hover:text-green-500 dark:hover:text-green-400" title="Start timer">
                    <PlayIcon className="h-3.5 w-3.5" />
                  </button>
                )}
                <button onClick={() => onSubtaskEdit(st)} className="text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400" title="Edit subtask">
                  <PencilSquareIcon className="h-3.5 w-3.5" />
                </button>
                <button onClick={() => onSubtaskDelete(st)} className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400" title="Delete subtask">
                  <TrashIcon className="h-3.5 w-3.5" />
                </button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// ===== Sortable Subtask Item =====

interface SortableSubtaskItemProps {
  subtask: TaskItem;
  onToggleStatus: (st: TaskItem) => void;
  onDelete: (id: string) => void;
}

const SortableSubtaskItem: React.FC<SortableSubtaskItemProps> = ({ subtask: st, onToggleStatus, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: st.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2 py-1.5 px-2 rounded bg-gray-50 dark:bg-gray-700/50 group">
      <button type="button" className="flex-shrink-0 cursor-grab active:cursor-grabbing text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 touch-none" {...attributes} {...listeners}>
        <Bars3Icon className="h-4 w-4" />
      </button>
      <button type="button" onClick={() => onToggleStatus(st)}>
        {st.status === 'done' ? (
          <CheckCircleSolid className="h-4 w-4 text-green-500" />
        ) : (
          <CheckCircleIcon className="h-4 w-4 text-gray-300 dark:text-gray-500 hover:text-green-500" />
        )}
      </button>
      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${PRIORITY_CONFIG[st.priority].dotColor}`} />
      <span className={`flex-1 text-sm truncate ${st.status === 'done' ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
        {st.title}
      </span>
      {st.estimated_hours != null && st.estimated_hours > 0 && (
        <span className="text-xs text-gray-500 dark:text-gray-400">
          {st.actual_hours ? `${st.actual_hours}h /` : ''} {st.estimated_hours}h
        </span>
      )}
      {st.date_due && (
        <span className={`text-xs ${isOverdue(st.date_due, st.status) ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
          {formatDate(st.date_due)}
        </span>
      )}
      <button type="button" onClick={() => onDelete(st.id)}
        className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity">
        <TrashIcon className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

// ===== Add/Edit Task Modal =====

interface AddEditTaskModalProps {
  task: TaskItem | null;
  projectId: string | null;
  projects: any[];
  projectsByClient: Record<string, any[]>;
  categories: any[];
  onSave: (data: TaskCreate | TaskUpdate) => Promise<void>;
  onClose: () => void;
}

const AddEditTaskModal: React.FC<AddEditTaskModalProps> = ({ task, projectId, projects, projectsByClient, categories, onSave, onClose }) => {
  const [title, setTitle] = useState(task?.title || '');
  const [description, setDescription] = useState(task?.description || '');
  const [formProjectId, setFormProjectId] = useState(task?.project_id || projectId || '');
  const [formStatus, setFormStatus] = useState<TaskStatus>(task?.status as TaskStatus || 'todo');
  const [priority, setPriority] = useState<TaskPriority>(task?.priority as TaskPriority || 'high');
  const [categoryId, setCategoryId] = useState(task?.category_id || '');
  const [subproject, setSubproject] = useState(task?.subproject_id || '');
  const [dateDue, setDateDue] = useState(task?.date_due || '');
  const [estimatedHours, setEstimatedHours] = useState(task?.estimated_hours?.toString() || '');
  const [tags, setTags] = useState(task?.tags?.join(', ') || '');
  const [actualHours, setActualHours] = useState(task?.actual_hours?.toString() || '');
  const [actualHoursOverride, setActualHoursOverride] = useState(task?.actual_hours_override || false);
  const [saving, setSaving] = useState(false);
  const [modalCategories, setModalCategories] = useState(categories);
  const [modalSubprojects, setModalSubprojects] = useState<ProjectSubproject[]>([]);
  const [modalAttachments, setModalAttachments] = useState<TaskAttachment[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const modalFileInputRef = React.useRef<HTMLInputElement>(null);
  const [modalLinks, setModalLinks] = useState<TaskLinkItem[]>([]);
  const [newLinkUrl, setNewLinkUrl] = useState('');
  const [newLinkTitle, setNewLinkTitle] = useState('');
  const [estimatedHoursOverride, setEstimatedHoursOverride] = useState(task?.estimated_hours_override || false);

  // Subtask management (edit mode only)
  const [modalSubtasks, setModalSubtasks] = useState<TaskItem[]>([]);
  const [showAddSubtask, setShowAddSubtask] = useState(false);
  const defaultSubtaskFields = {
    title: '', description: '', status: 'todo' as TaskStatus,
    priority: (task?.priority || 'medium') as TaskPriority,
    category_id: task?.category_id || '',
    subproject_id: task?.subproject_id || '',
    date_due: '', estimated_hours: '', tags: '',
  };
  const [newSubtask, setNewSubtask] = useState(defaultSubtaskFields);
  const [savingSubtask, setSavingSubtask] = useState(false);

  // Fetch categories and subprojects when project changes
  useEffect(() => {
    if (formProjectId) {
      categoriesAPI.list(formProjectId).then(setModalCategories).catch(() => {});
      subprojectsAPI.list(formProjectId).then(setModalSubprojects).catch(() => {});
    }
  }, [formProjectId]);

  // Load attachments, links, and subtasks when editing an existing task
  useEffect(() => {
    if (task) {
      taskAttachmentsAPI.list(task.id).then(setModalAttachments).catch(() => {});
      taskLinksAPI.list(task.id).then(setModalLinks).catch(() => {});
      tasksAPI.list({ parent_task_id: task.id }).then(setModalSubtasks).catch(() => {});
    }
  }, [task]);

  const handleUpload = async (files: FileList) => {
    if (!task) return;
    setIsUploading(true);
    try {
      for (let i = 0; i < files.length; i++) {
        await taskAttachmentsAPI.upload(task.id, files[i]);
      }
      const updated = await taskAttachmentsAPI.list(task.id);
      setModalAttachments(updated);
    } catch { /* ignore */ }
    setIsUploading(false);
  };

  const handleDownload = async (att: TaskAttachment) => {
    try {
      const blob = await taskAttachmentsAPI.download(att.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = att.original_filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!task) return;
    try {
      await taskAttachmentsAPI.delete(attachmentId);
      setModalAttachments(prev => prev.filter(a => a.id !== attachmentId));
    } catch { /* ignore */ }
  };

  const handleAddLink = async () => {
    if (!task || !newLinkUrl.trim()) return;
    try {
      const link = await taskLinksAPI.create(task.id, {
        url: newLinkUrl.trim(),
        title: newLinkTitle.trim() || undefined,
      });
      setModalLinks(prev => [...prev, link]);
      setNewLinkUrl('');
      setNewLinkTitle('');
    } catch { /* ignore */ }
  };

  const handleDeleteLink = async (linkId: string) => {
    try {
      await taskLinksAPI.delete(linkId);
      setModalLinks(prev => prev.filter(l => l.id !== linkId));
    } catch { /* ignore */ }
  };

  const handleAddSubtask = async () => {
    if (!task || !newSubtask.title.trim()) return;
    setSavingSubtask(true);
    try {
      await tasksAPI.create({
        project_id: task.project_id,
        parent_task_id: task.id,
        title: newSubtask.title.trim(),
        description: newSubtask.description.trim() || undefined,
        status: newSubtask.status,
        priority: newSubtask.priority,
        category_id: newSubtask.category_id || undefined,
        subproject_id: newSubtask.subproject_id || undefined,
        date_due: newSubtask.date_due || undefined,
        estimated_hours: newSubtask.estimated_hours ? parseFloat(newSubtask.estimated_hours) : undefined,
        tags: newSubtask.tags ? newSubtask.tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
      });
      const updated = await tasksAPI.list({ parent_task_id: task.id });
      setModalSubtasks(updated);
      setNewSubtask(defaultSubtaskFields);
      setShowAddSubtask(false);
    } catch { /* ignore */ }
    setSavingSubtask(false);
  };

  const handleDeleteSubtask = async (subtaskId: string) => {
    if (!task || !window.confirm('Delete this subtask?')) return;
    try {
      await tasksAPI.delete(subtaskId);
      setModalSubtasks(prev => prev.filter(s => s.id !== subtaskId));
    } catch { /* ignore */ }
  };

  const handleToggleSubtaskStatus = async (subtask: TaskItem) => {
    const newStatus: TaskStatus = subtask.status === 'done' ? 'todo' : 'done';
    try {
      await tasksAPI.updateStatus(subtask.id, newStatus);
      setModalSubtasks(prev => prev.map(s => s.id === subtask.id ? { ...s, status: newStatus } : s));
    } catch { /* ignore */ }
  };

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleSubtaskDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = modalSubtasks.findIndex(s => s.id === active.id);
    const newIndex = modalSubtasks.findIndex(s => s.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(modalSubtasks, oldIndex, newIndex);
    setModalSubtasks(reordered);
    try {
      await tasksAPI.batchReorder(reordered.map(s => s.id));
    } catch { /* ignore */ }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim()) return;
    setSaving(true);
    try {
      if (task) {
        // Update
        await onSave({
          title: title.trim(),
          description: description.trim() || null,
          status: formStatus,
          priority,
          category_id: categoryId || null,
          subproject_id: subproject || null,
          date_due: dateDue || '',
          estimated_hours: estimatedHours ? parseFloat(estimatedHours) : null,
          actual_hours: actualHours ? parseFloat(actualHours) : null,
          actual_hours_override: actualHoursOverride,
          estimated_hours_override: estimatedHoursOverride,
          tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : [],
        } as TaskUpdate);
      } else {
        // Create
        await onSave({
          project_id: formProjectId,
          title: title.trim(),
          description: description.trim() || undefined,
          status: formStatus,
          priority,
          category_id: categoryId || undefined,
          subproject_id: subproject || undefined,
          date_due: dateDue || undefined,
          estimated_hours: estimatedHours ? parseFloat(estimatedHours) : undefined,
          tags: tags ? tags.split(',').map(t => t.trim()).filter(Boolean) : undefined,
        } as TaskCreate);
      }
    } catch { /* error handled in store */ }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          {task ? 'Edit Task' : 'Add Task'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title *</label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} required maxLength={500}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent" />
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
            <textarea value={description} onChange={e => setDescription(e.target.value)} rows={3}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent" />
          </div>

          {/* Project (only for new tasks) */}
          {!task && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Project *</label>
              <select value={formProjectId} onChange={e => setFormProjectId(e.target.value)} required
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent">
                <option value="">Select project...</option>
                {Object.entries(projectsByClient).map(([clientName, projs]) => (
                  <optgroup key={clientName} label={clientName}>
                    {projs.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </optgroup>
                ))}
              </select>
            </div>
          )}

          {/* Status + Priority row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
              <select value={formStatus} onChange={e => setFormStatus(e.target.value as TaskStatus)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent">
                {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_CONFIG[s].label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Priority</label>
              <select value={priority} onChange={e => setPriority(e.target.value as TaskPriority)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent">
                {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>

          {/* Category + Subproject row */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Category</label>
              <select value={categoryId} onChange={e => setCategoryId(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent">
                <option value="">None</option>
                {modalCategories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subproject</label>
              <select value={subproject} onChange={e => setSubproject(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent">
                <option value="">None</option>
                {modalSubprojects.filter(sp => sp.is_active).map(sp => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
              </select>
            </div>
          </div>

          {/* Due date + Estimated hours */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Due Date</label>
              <input type="date" value={dateDue} onChange={e => setDateDue(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent dark:[color-scheme:dark]" />
            </div>
            <div>
              <div className="flex items-center gap-3 mb-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Estimated Hours</label>
                {task && modalSubtasks.length > 0 && (
                  <label className="flex items-center gap-1.5 cursor-pointer">
                    <input type="checkbox" checked={estimatedHoursOverride} onChange={e => setEstimatedHoursOverride(e.target.checked)}
                      className="rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500" />
                    <span className="text-xs text-gray-500 dark:text-gray-400">Override subtask total</span>
                  </label>
                )}
              </div>
              <input type="number" step="0.25" min="0" value={estimatedHours} onChange={e => setEstimatedHours(e.target.value)}
                disabled={task != null && modalSubtasks.length > 0 && !estimatedHoursOverride}
                placeholder={task && modalSubtasks.length > 0 && !estimatedHoursOverride
                  ? `Subtask total: ${modalSubtasks.reduce((s, st) => s + (st.estimated_hours || 0), 0)}h`
                  : 'Enter hours'
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed" />
            </div>
          </div>

          {/* Actual Hours + Override (edit only) */}
          {task && (
            <div>
              <div className="flex items-center gap-3 mb-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Actual Hours</label>
                <label className="flex items-center gap-1.5 cursor-pointer">
                  <input type="checkbox" checked={actualHoursOverride} onChange={e => setActualHoursOverride(e.target.checked)}
                    className="rounded border-gray-300 dark:border-gray-600 text-green-600 focus:ring-green-500" />
                  <span className="text-xs text-gray-500 dark:text-gray-400">Override {modalSubtasks.length > 0 ? 'subtask total + ' : ''}linked time entries</span>
                </label>
              </div>
              <input type="number" step="0.25" min="0" value={actualHours} onChange={e => setActualHours(e.target.value)}
                disabled={!actualHoursOverride}
                placeholder={actualHoursOverride ? 'Enter hours manually'
                  : modalSubtasks.length > 0
                    ? `Subtask total: ${modalSubtasks.reduce((s, st) => s + (st.actual_hours || 0), 0)}h + linked entries`
                    : 'Calculated from linked time entries'
                }
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed" />
            </div>
          )}

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Tags <span className="text-gray-400 dark:text-gray-500 font-normal">(comma-separated)</span></label>
            <input type="text" value={tags} onChange={e => setTags(e.target.value)} placeholder="e.g. frontend, urgent, design"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent" />
          </div>

          {/* Attachments (edit only) */}
          {task && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Attachments ({modalAttachments.length}/10)</label>
                <input ref={modalFileInputRef} type="file" className="hidden"
                  accept=".pdf,.docx,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp" multiple
                  onChange={(e) => { if (e.target.files) handleUpload(e.target.files); e.target.value = ''; }}
                />
                <button type="button" onClick={() => modalFileInputRef.current?.click()} disabled={isUploading || modalAttachments.length >= 10}
                  className="text-xs text-green-600 hover:text-green-700 font-medium disabled:opacity-50">
                  <ArrowUpTrayIcon className="h-3.5 w-3.5 inline mr-1" />Upload
                </button>
              </div>
              {isUploading && <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Uploading...</p>}
              <div className="space-y-1">
                {modalAttachments.map(att => (
                  <div key={att.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-gray-50 dark:bg-gray-700/50">
                    <div className="flex items-center gap-2 min-w-0">
                      <PaperClipIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{att.original_filename}</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">{formatFileSize(att.file_size)}</span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button type="button" onClick={() => handleDownload(att)} className="text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400">
                        <ArrowDownTrayIcon className="h-4 w-4" />
                      </button>
                      <button type="button" onClick={() => handleDeleteAttachment(att.id)} className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400">
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
                {modalAttachments.length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 py-1">No attachments. Click Upload to add files.</p>
                )}
              </div>
            </div>
          )}

          {/* URL Links (edit only) */}
          {task && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">URL Links ({modalLinks.length}/10)</label>
              <div className="space-y-1 mb-2">
                {modalLinks.map(link => (
                  <div key={link.id} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700/50 rounded px-3 py-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <LinkIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="truncate text-blue-600 dark:text-blue-400 hover:underline"
                        title={link.url}
                      >
                        {link.title || link.url}
                      </a>
                    </div>
                    <button type="button" onClick={() => handleDeleteLink(link.id)} className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 flex-shrink-0">
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                {modalLinks.length === 0 && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 py-1">No links. Add a URL below.</p>
                )}
              </div>
              {modalLinks.length < 10 && (
                <div className="flex gap-2">
                  <input type="url" value={newLinkUrl} onChange={e => setNewLinkUrl(e.target.value)}
                    placeholder="https://..."
                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  <input type="text" value={newLinkTitle} onChange={e => setNewLinkTitle(e.target.value)}
                    placeholder="Title (optional)"
                    className="w-36 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  <button type="button" onClick={handleAddLink} disabled={!newLinkUrl.trim()}
                    className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 rounded-lg">
                    <PlusIcon className="h-4 w-4" />Add
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Subtasks (edit only) */}
          {task && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Subtasks ({modalSubtasks.length})
                  {modalSubtasks.length > 0 && (
                    <span className="ml-2 text-xs text-gray-400 dark:text-gray-500 font-normal">
                      {modalSubtasks.filter(s => s.status === 'done').length}/{modalSubtasks.length} done
                    </span>
                  )}
                </label>
                <button type="button" onClick={() => setShowAddSubtask(!showAddSubtask)}
                  className="text-xs text-green-600 hover:text-green-700 font-medium">
                  {showAddSubtask ? 'Cancel' : '+ Add Subtask'}
                </button>
              </div>

              {/* Existing subtasks list (drag-and-drop sortable) */}
              {modalSubtasks.length > 0 && (
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleSubtaskDragEnd}>
                  <SortableContext items={modalSubtasks.map(s => s.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1 mb-2">
                      {modalSubtasks.map(st => (
                        <SortableSubtaskItem
                          key={st.id}
                          subtask={st}
                          onToggleStatus={handleToggleSubtaskStatus}
                          onDelete={handleDeleteSubtask}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}

              {/* Add Subtask Form */}
              {showAddSubtask && (
                <div className="border border-green-200 dark:border-green-800 rounded-lg p-3 bg-green-50/50 dark:bg-green-900/10 space-y-3">
                  <input type="text" value={newSubtask.title} onChange={e => setNewSubtask({ ...newSubtask, title: e.target.value })}
                    placeholder="Subtask title *" autoFocus
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddSubtask(); } }}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  <textarea value={newSubtask.description} onChange={e => setNewSubtask({ ...newSubtask, description: e.target.value })}
                    placeholder="Description (optional)" rows={2}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                  <div className="grid grid-cols-2 gap-2">
                    <select value={newSubtask.priority} onChange={e => setNewSubtask({ ...newSubtask, priority: e.target.value as TaskPriority })}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                      {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                    <select value={newSubtask.category_id} onChange={e => setNewSubtask({ ...newSubtask, category_id: e.target.value })}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                      <option value="">No category</option>
                      {modalCategories.map((c: any) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <select value={newSubtask.subproject_id} onChange={e => setNewSubtask({ ...newSubtask, subproject_id: e.target.value })}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white">
                      <option value="">No subproject</option>
                      {modalSubprojects.filter(sp => sp.is_active).map(sp => <option key={sp.id} value={sp.id}>{sp.name}</option>)}
                    </select>
                    <input type="date" value={newSubtask.date_due} onChange={e => setNewSubtask({ ...newSubtask, date_due: e.target.value })}
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white dark:[color-scheme:dark]" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input type="number" step="0.25" min="0" value={newSubtask.estimated_hours}
                      onChange={e => setNewSubtask({ ...newSubtask, estimated_hours: e.target.value })}
                      placeholder="Estimated hours"
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                    <input type="text" value={newSubtask.tags} onChange={e => setNewSubtask({ ...newSubtask, tags: e.target.value })}
                      placeholder="Tags (comma-separated)"
                      className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white" />
                  </div>
                  <div className="flex justify-end gap-2">
                    <button type="button" onClick={() => setShowAddSubtask(false)}
                      className="px-3 py-1.5 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                      Cancel
                    </button>
                    <button type="button" onClick={handleAddSubtask} disabled={savingSubtask || !newSubtask.title.trim()}
                      className="px-3 py-1.5 text-xs bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 font-medium">
                      {savingSubtask ? 'Adding...' : 'Add Subtask'}
                    </button>
                  </div>
                </div>
              )}

              {modalSubtasks.length === 0 && !showAddSubtask && (
                <p className="text-xs text-gray-500 dark:text-gray-400 py-1">No subtasks yet. Click "+ Add Subtask" to create one.</p>
              )}
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">Cancel</button>
            <button type="submit" disabled={saving || !title.trim()}
              className="px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
              {saving ? 'Saving...' : task ? 'Save Changes' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ===== Task Detail Modal =====

interface TaskDetailModalProps {
  task: TaskWithSubtasks;
  attachments: TaskAttachment[];
  urlLinks: TaskLinkItem[];
  timeEntries: TaskTimeEntryLink[];
  categories: any[];
  isUploadingAttachment: boolean;
  onClose: () => void;
  onUpdateStatus: (status: TaskStatus) => Promise<void>;
  onEdit: () => void;
  onDelete: () => void;
  onUploadAttachment: (files: FileList) => void;
  onDownloadAttachment: (att: TaskAttachment) => void;
  onDeleteAttachment: (id: string) => void;
  onAddUrlLink: (url: string, title?: string) => Promise<void>;
  onDeleteUrlLink: (linkId: string) => Promise<void>;
  onLogTime: () => void;
  onLinkEntry: () => void;
  onUnlinkEntry: (timeEntryId: string) => void;
  onAddSubtask: (title: string) => Promise<void>;
  onSubtaskStatusToggle: (subtask: TaskItem) => Promise<void>;
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  task, attachments, urlLinks, timeEntries, categories, isUploadingAttachment,
  onClose, onUpdateStatus, onEdit, onDelete,
  onUploadAttachment, onDownloadAttachment, onDeleteAttachment,
  onAddUrlLink, onDeleteUrlLink,
  onLogTime, onLinkEntry, onUnlinkEntry,
  onAddSubtask, onSubtaskStatusToggle,
}) => {
  const [newSubtaskTitle, setNewSubtaskTitle] = useState('');
  const [detailNewLinkUrl, setDetailNewLinkUrl] = useState('');
  const [detailNewLinkTitle, setDetailNewLinkTitle] = useState('');
  const [showSubtaskInput, setShowSubtaskInput] = useState(false);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const totalLinkedHours = timeEntries.reduce((sum, e) => sum + e.hours, 0);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className={`w-2.5 h-2.5 rounded-full ${PRIORITY_CONFIG[task.priority].dotColor}`} />
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_CONFIG[task.status].bgColor} ${STATUS_CONFIG[task.status].color}`}>
                  {STATUS_CONFIG[task.status].label}
                </span>
                {task.category_name && (
                  <span className="text-xs px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: task.category_color || '#6B7280' }}>
                    {task.category_name}
                  </span>
                )}
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">{task.title}</h2>
              {task.project_name && (
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                  {task.client_name} / {task.project_name}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2 ml-4">
              <button onClick={onEdit} className="text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400"><PencilSquareIcon className="h-5 w-5" /></button>
              <button onClick={onDelete} className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400"><TrashIcon className="h-5 w-5" /></button>
              <button onClick={onClose} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"><XMarkIcon className="h-5 w-5" /></button>
            </div>
          </div>

          {/* Quick status buttons */}
          <div className="flex flex-wrap gap-2 mt-4">
            {STATUS_ORDER.map(s => (
              <button
                key={s}
                onClick={() => onUpdateStatus(s)}
                className={`text-xs px-3 py-1 rounded-full font-medium transition-colors ${
                  task.status === s
                    ? `${STATUS_CONFIG[s].bgColor} ${STATUS_CONFIG[s].color} ring-2 ring-offset-1 ring-green-500 dark:ring-offset-gray-800`
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600'
                }`}
              >
                {STATUS_CONFIG[s].label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Description */}
          {task.description && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Description</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 whitespace-pre-wrap">{task.description}</p>
            </div>
          )}

          {/* Dates & Hours */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Details</h3>
              <dl className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Priority</dt>
                  <dd className={PRIORITY_CONFIG[task.priority].color}>{PRIORITY_CONFIG[task.priority].label}</dd>
                </div>
                {task.date_due && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Due</dt>
                    <dd className={isOverdue(task.date_due, task.status) ? 'text-red-600 font-medium' : 'text-gray-900 dark:text-white'}>
                      {formatDate(task.date_due)}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Created</dt>
                  <dd className="text-gray-900 dark:text-white">{formatDate(task.date_created)}</dd>
                </div>
                {task.date_completed && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Completed</dt>
                    <dd className="text-green-600">{formatDate(task.date_completed)}</dd>
                  </div>
                )}
              </dl>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Hours</h3>
              <dl className="space-y-1 text-sm">
                {(task.estimated_hours != null || task.subtask_estimated_hours_total != null) && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">Estimated</dt>
                    <dd className="text-gray-900 dark:text-white">
                      {task.estimated_hours_override || !task.subtask_estimated_hours_total
                        ? `${task.estimated_hours ?? 0}h`
                        : <>{task.subtask_estimated_hours_total}h <span className="text-xs text-green-500">&Sigma;</span></>
                      }
                      {task.estimated_hours_override && <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">(manual)</span>}
                    </dd>
                  </div>
                )}
                <div className="flex justify-between">
                  <dt className="text-gray-500 dark:text-gray-400">Actual</dt>
                  <dd className="text-gray-900 dark:text-white font-medium">
                    {task.actual_hours_override || !task.subtask_actual_hours_total
                      ? `${task.actual_hours || 0}h`
                      : <>{task.subtask_actual_hours_total}h <span className="text-xs text-green-500">&Sigma;</span></>
                    }
                    {task.actual_hours_override && <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">(manual)</span>}
                  </dd>
                </div>
                {totalLinkedHours > 0 && !task.actual_hours_override && (
                  <div className="flex justify-between">
                    <dt className="text-gray-500 dark:text-gray-400">From entries</dt>
                    <dd className="text-gray-600 dark:text-gray-400">{totalLinkedHours.toFixed(2)}h</dd>
                  </div>
                )}
              </dl>
            </div>
          </div>

          {/* Tags */}
          {task.tags && task.tags.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Tags</h3>
              <div className="flex flex-wrap gap-1">
                {task.tags.map((tag, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Dates Worked */}
          {task.dates_worked && task.dates_worked.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Dates Worked</h3>
              <div className="flex flex-wrap gap-1">
                {task.dates_worked.map((d, i) => (
                  <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400">
                    {formatDate(d)}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Subtasks */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Subtasks {task.subtasks.length > 0 && `(${task.subtask_done_count}/${task.subtask_count})`}
              </h3>
              <button onClick={() => setShowSubtaskInput(true)} className="text-xs text-green-600 hover:text-green-700 font-medium">
                + Add Subtask
              </button>
            </div>
            <div className="space-y-1">
              {task.subtasks.map(st => (
                <div key={st.id} className="flex items-center gap-2 py-1">
                  <button onClick={() => onSubtaskStatusToggle(st)}>
                    {st.status === 'done' ? (
                      <CheckCircleSolid className="h-4 w-4 text-green-500" />
                    ) : (
                      <CheckCircleIcon className="h-4 w-4 text-gray-300 dark:text-gray-600 hover:text-green-500 dark:hover:text-green-400" />
                    )}
                  </button>
                  <span className={`text-sm ${st.status === 'done' ? 'line-through text-gray-400 dark:text-gray-500' : 'text-gray-700 dark:text-gray-300'}`}>
                    {st.title}
                  </span>
                  {st.date_due && (
                    <span className={`text-xs ml-auto ${isOverdue(st.date_due, st.status) ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
                      {formatDate(st.date_due)}
                    </span>
                  )}
                </div>
              ))}
              {showSubtaskInput && (
                <div className="flex items-center gap-2 py-1">
                  <CheckCircleIcon className="h-4 w-4 text-gray-300" />
                  <input
                    type="text" value={newSubtaskTitle} onChange={e => setNewSubtaskTitle(e.target.value)}
                    onKeyDown={async (e) => {
                      if (e.key === 'Enter' && newSubtaskTitle.trim()) {
                        await onAddSubtask(newSubtaskTitle.trim());
                        setNewSubtaskTitle('');
                      }
                      if (e.key === 'Escape') { setShowSubtaskInput(false); setNewSubtaskTitle(''); }
                    }}
                    placeholder="Subtask title..."
                    className="flex-1 text-sm rounded border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white py-1 px-2"
                    autoFocus
                  />
                  <button onClick={() => { setShowSubtaskInput(false); setNewSubtaskTitle(''); }} className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300">
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Attachments */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Attachments ({attachments.length}/10)
              </h3>
              <input ref={fileInputRef} type="file" className="hidden"
                accept=".pdf,.docx,.pptx,.txt,.jpg,.jpeg,.png,.gif,.webp" multiple
                onChange={(e) => { if (e.target.files) onUploadAttachment(e.target.files); e.target.value = ''; }}
              />
              <button onClick={() => fileInputRef.current?.click()} disabled={isUploadingAttachment || attachments.length >= 10}
                className="text-xs text-green-600 hover:text-green-700 font-medium disabled:opacity-50">
                <ArrowUpTrayIcon className="h-3.5 w-3.5 inline mr-1" />
                Upload
              </button>
            </div>
            {isUploadingAttachment && <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">Uploading...</p>}
            <div className="space-y-1">
              {attachments.map(att => (
                <div key={att.id} className="flex items-center justify-between py-1.5 px-2 rounded bg-gray-50 dark:bg-gray-700/50">
                  <div className="flex items-center gap-2 min-w-0">
                    <PaperClipIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{att.original_filename}</span>
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">{formatFileSize(att.file_size)}</span>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => onDownloadAttachment(att)} className="text-gray-400 dark:text-gray-500 hover:text-blue-500 dark:hover:text-blue-400">
                      <ArrowDownTrayIcon className="h-4 w-4" />
                    </button>
                    <button onClick={() => onDeleteAttachment(att.id)} className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400">
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              {attachments.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 py-1">No attachments yet.</p>
              )}
            </div>
          </div>

          {/* URL Links */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                URL Links ({urlLinks.length}/10)
              </h3>
            </div>
            <div className="space-y-1 mb-2">
              {urlLinks.map(link => (
                <div key={link.id} className="flex items-center justify-between text-sm bg-gray-50 dark:bg-gray-700/50 rounded px-3 py-1.5">
                  <div className="flex items-center gap-2 min-w-0">
                    <LinkIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="truncate text-blue-600 dark:text-blue-400 hover:underline"
                      title={link.url}
                    >
                      {link.title || link.url}
                    </a>
                  </div>
                  <button onClick={() => onDeleteUrlLink(link.id)} className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 flex-shrink-0">
                    <TrashIcon className="h-3.5 w-3.5" />
                  </button>
                </div>
              ))}
              {urlLinks.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 py-1">No links yet.</p>
              )}
            </div>
            {urlLinks.length < 10 && (
              <div className="flex gap-2">
                <input type="url" value={detailNewLinkUrl} onChange={e => setDetailNewLinkUrl(e.target.value)}
                  placeholder="https://..."
                  className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                <input type="text" value={detailNewLinkTitle} onChange={e => setDetailNewLinkTitle(e.target.value)}
                  placeholder="Title (optional)"
                  className="w-36 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-transparent" />
                <button
                  onClick={async () => {
                    if (detailNewLinkUrl.trim()) {
                      await onAddUrlLink(detailNewLinkUrl.trim(), detailNewLinkTitle.trim() || undefined);
                      setDetailNewLinkUrl('');
                      setDetailNewLinkTitle('');
                    }
                  }}
                  disabled={!detailNewLinkUrl.trim()}
                  className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-white bg-green-600 hover:bg-green-700 disabled:bg-gray-400 rounded-lg">
                  <PlusIcon className="h-4 w-4" />Add
                </button>
              </div>
            )}
          </div>

          {/* Linked Time Entries */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Linked Time Entries ({timeEntries.length})
              </h3>
              <div className="flex gap-2">
                <button onClick={onLinkEntry} className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                  <LinkIcon className="h-3.5 w-3.5 inline mr-1" />Link Entry
                </button>
                <button onClick={onLogTime} className="text-xs text-green-600 hover:text-green-700 font-medium">
                  <ClockIcon className="h-3.5 w-3.5 inline mr-1" />Log Time
                </button>
              </div>
            </div>
            <div className="space-y-1">
              {timeEntries.map(e => (
                <div key={e.time_entry_id} className="flex items-center justify-between py-1.5 px-2 rounded bg-gray-50 dark:bg-gray-700/50">
                  <div className="flex items-center gap-2 min-w-0">
                    <ClockIcon className="h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                    <span className="text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">{formatDate(e.date)}</span>
                    <span className="text-sm text-gray-700 dark:text-gray-300 truncate">{e.description}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{e.hours}h</span>
                    <button onClick={() => onUnlinkEntry(e.time_entry_id)} className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400">
                      <XMarkIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
              {timeEntries.length === 0 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 py-1">No linked time entries.</p>
              )}
              {timeEntries.length > 0 && (
                <div className="flex justify-end pt-1">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    Total: {totalLinkedHours.toFixed(2)}h
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
